import React, { useMemo } from 'react';
import { Text } from 'components/Text';
import { OrderItemValues } from 'types/order';
import {
  XCircleIcon,
  DocumentSearchIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  ExclamationIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import { formatCurrency, getStoreCurrencyFormatter } from 'utils';
import { useStore } from 'context/store-context';
import { ClaimItemResolvedStatusEnum, ClaimItemValues } from 'types/claim';
import {
  ClaimResolvedStatusBadges,
  statusColorClasses,
} from 'containers/NestedContainers/Claim';
import ReplaceResolutionVariant from './ReplaceResolutionVariant';

type OrderDetailProps = {
  order: OrderItemValues;
  onRemoveItem?: () => void;
  showCategories?: boolean;
  showClaimItemResolution?: boolean;
  disabledClamResolution?: boolean;
  selectedCategory?: string;
  claimItem: ClaimItemValues;
  isSelected?: boolean;
  hasBorderBottom?: boolean;
  quantityEditable?: boolean;
  resolution?: ClaimItemResolvedStatusEnum | undefined;
  onViewResolution: () => void;
  onResolvePartialItems?: (claimItem: ClaimItemValues) => void;
};

const categories = [
  { title: 'Lost', icon: DocumentSearchIcon, key: 'LOST' },
  { title: 'Stolen', icon: ExclamationIcon, key: 'STOLEN' },
  { title: 'Damaged', icon: XCircleIcon, key: 'DAMAGED' },
  { title: 'Late', icon: ClockIcon, key: 'LATE' },
  { title: 'None', icon: QuestionMarkCircleIcon, key: 'OTHER' },
];

export const OrderDetail = ({
  order,
  selectedCategory,
  showCategories,
  claimItem,
  showClaimItemResolution,
  onViewResolution,
  resolution,
  disabledClamResolution,
  hasBorderBottom = true,
  quantityEditable = false,
  onResolvePartialItems,
}: OrderDetailProps) => {
  const { storeId, storeProperties } = useStore();

  const category = categories.find((c) => c.key === selectedCategory);

  const showPartialResolution = useMemo(() => {
    if (!claimItem || !claimItem.settlement) return false;
    return claimItem?.quantity !== claimItem?.settlement?.quantity;
  }, [claimItem]);

  const getQuantityDisplay = () => {
    if (showPartialResolution) {
      const settledQty = claimItem.settlement?.quantity || 0;
      return `${settledQty} of ${claimItem.quantity}`;
    }
    return claimItem.quantity;
  };

  const getStatusText = () => {
    if (resolution === ClaimItemResolvedStatusEnum.IN_REVIEW) {
      return 'In Review';
    }
    if (claimItem.settlement) {
      return claimItem.resolution;
    }
    return 'Pending Resolution';
  };

  return (
    <div
      className={classNames(
        'w-full flex gap-3 p-4 bg-gray-50 rounded-sm border border-gray-200',
        resolution ? 'cursor-pointer' : '',
      )}
      onClick={resolution && onViewResolution}
    >
      {/* Product Image */}
      <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
        <img
          className="w-full h-full object-cover"
          src={order.imageSrc}
          alt={order.title || 'Product'}
        />
      </div>

      {/* Content Container */}
      <div className="flex-1 min-w-0">
        {/* Header Section */}
        <div className="flex justify-between items-start gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm text-gray-900 leading-5 mb-1 truncate">
              {order.title}
            </h4>
            {!resolution && (
              <div className="mb-2">
                <ReplaceResolutionVariant
                  storeId={String(storeId)}
                  claimItem={claimItem}
                />
              </div>
            )}
            <div className="text-xs text-gray-500 mb-2">
              SKU: {order.sku || 'N/A'}
            </div>
          </div>

          {/* Price and Quantity Section */}
          <div className="text-right flex-shrink-0">
            <div className="font-semibold text-sm mb-1">
              {getStoreCurrencyFormatter(
                storeProperties?.currency,
                formatCurrency(order.price),
              )}{' '}
              × {getQuantityDisplay()}
            </div>
          </div>
        </div>

        {/* Status Section */}
        <div className="flex justify-between items-center mb-3">
          <div
            className={`px-2 py-1 rounded-lg text-xs font-medium ${statusColorClasses(
              resolution || ClaimItemResolvedStatusEnum.IN_REVIEW,
            )}`}
          >
            {getStatusText()}
          </div>

          {/* Total Price */}
          {order.subtotal && (
            <div className="text-right">
              <Text
                value={getStoreCurrencyFormatter(
                  storeProperties?.currency,
                  formatCurrency(
                    order.price * Number(claimItem.quantity) -
                      (order?.discountTotal ?? 0),
                  ),
                )}
                type="body"
                className="font-semibold text-gray-900"
              />
            </div>
          )}
        </div>

        {/* Partial Resolution Action */}
        {showPartialResolution && claimItem?.quantity && (
          <button
            onClick={() => onResolvePartialItems?.(claimItem)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer mb-3 transition-colors"
          >
            Resolve remaining{' '}
            {claimItem?.quantity - (claimItem.settlement?.quantity || 0)} items
          </button>
        )}

        {/* Categories Section */}
        {showCategories && category && (
          <div className="mb-3">
            <div
              className={classNames(
                'inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-colors',
                selectedCategory === category.key
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 bg-white hover:bg-gray-50',
              )}
            >
              <category.icon
                className={classNames(
                  'h-4 w-4',
                  selectedCategory === category.key
                    ? 'text-blue-600'
                    : 'text-gray-600',
                )}
              />
              <Text
                value={category.title}
                type="sm"
                className={classNames(
                  selectedCategory === category.key
                    ? 'text-blue-600 font-medium'
                    : 'text-gray-600',
                )}
              />
            </div>
          </div>
        )}

        {/* Resolution Status Badge */}
        {resolution && (
          <div className="flex justify-end">
            <ClaimResolvedStatusBadges status={resolution} />
          </div>
        )}
      </div>
    </div>
  );
};
